import { formatMoney } from '../money';
import type { DistanceFeeSettings } from '../settings';
import { formatKm } from './correios';
import { roundKm } from './geo';

/**
 * Entrega própria (motoboy) cobrada por distância de rua estimada.
 * Taxa = base (cobre os primeiros `includedKm`) + km adicionais × `perKmCents`, proporcional ao km rodado,
 * arredondada para CIMA no múltiplo de `roundToCents`. Acima de `maxKm` a entrega local não atende.
 * Ex.: base R$ 7,00 até 3 km, R$ 1,50/km, 7,4 km de rua: 700 + 4,4 × 150 = 1.360 → arredonda (R$ 0,50) → R$ 14,00.
 */
export interface LocalDistanceQuote {
  available: boolean;
  reason: string | null;
  priceCents: number | null;
  roadKm: number;
  extraKm: number;
  extraCents: number;
  /** Antes do arredondamento. */
  rawCents: number;
  notes: string[];
}

export function quoteLocalByDistance(roadKm: number, s: DistanceFeeSettings): LocalDistanceQuote {
  const km = roundKm(Math.max(0, roadKm));
  if (km > s.maxKm) {
    return {
      available: false,
      reason: `Fora do raio de entrega (${formatKm(km)}, máximo ${formatKm(s.maxKm)})`,
      priceCents: null,
      roadKm: km,
      extraKm: 0,
      extraCents: 0,
      rawCents: 0,
      notes: [],
    };
  }
  const extraKm = roundKm(Math.max(0, km - s.includedKm));
  const extraCents = Math.round(extraKm * s.perKmCents);
  const raw = s.baseFeeCents + extraCents;
  const price = s.roundToCents > 0 ? Math.ceil(raw / s.roundToCents) * s.roundToCents : raw;
  const notes = [
    `Taxa base ${formatMoney(s.baseFeeCents)} cobre até ${formatKm(s.includedKm)}.`,
    extraKm > 0
      ? `${formatKm(extraKm)} a mais × ${formatMoney(s.perKmCents)} por km = ${formatMoney(extraCents)}.`
      : 'Sem km adicional.',
  ];
  if (price !== raw) notes.push(`Total ${formatMoney(raw)} arredondado para ${formatMoney(price)}.`);
  return { available: true, reason: null, priceCents: price, roadKm: km, extraKm, extraCents, rawCents: raw, notes };
}
